/**
 * スコアサーバー API クライアント
 */

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3001';

export interface RankCheckResponse {
  canRegister: boolean;
  estimatedRank: number;
  total: number;
  cutoffScore: number;
  monthlyRank: number;
  monthlyTotal: number;
}

export interface ScoreSubmitRequest {
  playerName: string;
  mapType: string;
  finalScore: number;
  turnCount: number;
  metadata: Record<string, unknown>;
  replayData: {
    initialState: unknown;
    moves: unknown[];
    moveLog?: unknown[];
  };
}

export interface ScoreSubmitResponse {
  success: boolean;
  scoreId?: number;
  rank?: number;
  hasReplay?: boolean;
  verification?: {
    immediate: string;
    message: string;
  };
  error?: string;
  code?: string;
}

/**
 * スコア登録可否チェック
 */
export async function checkScoreRank(
  mapType: string,
  score: number,
): Promise<RankCheckResponse> {
  const res = await fetch(`${API_BASE}/api/scores/${mapType}/check?score=${score}`);
  if (!res.ok) throw new Error(`API error: ${res.status}`);
  return res.json();
}

/**
 * スコア登録
 */
export async function submitScore(
  data: ScoreSubmitRequest,
): Promise<ScoreSubmitResponse> {
  const res = await fetch(`${API_BASE}/api/scores`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  return res.json();
}

/**
 * ML用リプレイアーカイブ送信（サイレント、失敗しても無視）
 */
export async function submitReplayArchive(data: {
  mapType: string;
  finalScore?: number;
  turnCount?: number;
  metadata?: Record<string, unknown>;
  replayData: { initialState: unknown; moves?: unknown[]; moveLog?: unknown[] };
}): Promise<void> {
  try {
    await fetch(`${API_BASE}/api/replays/archive`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  } catch {
    // サイレント — 失敗しても何もしない
  }
}

export interface RankingEntry {
  rank: number;
  playerName: string;
  finalScore: number;
  turnCount: number;
  playedAt: string;
  hasReplay: boolean;
  metadata: Record<string, unknown>;
}

export interface RankingResponse {
  mapType: string;
  period: string;
  noUndo: boolean;
  total: number;
  scores: RankingEntry[];
}

export interface ReplayResponse {
  replayId: number;
  scoreId: number;
  playerName: string;
  mapType: string;
  finalScore: number;
  rank: number;
  initialState: unknown;
  moves: Array<{ turn: number; G: unknown }>;
  moveLog?: unknown[];
}

/**
 * リプレイデータ取得
 */
export async function fetchReplay(
  mapType: string,
  rank: number,
  noUndo = false,
): Promise<ReplayResponse> {
  const prefix = noUndo ? '?noUndo=true' : '';
  const res = await fetch(`${API_BASE}/api/replays/${mapType}/${rank}${prefix}`);
  if (!res.ok) throw new Error(`API error: ${res.status}`);
  return res.json();
}

/**
 * ランキング取得
 */
export async function fetchRankings(
  mapType: string,
  period: 'all' | 'week' | 'month' = 'all',
  limit = 50,
  offset = 0,
  noUndo = false,
): Promise<RankingResponse> {
  const params = `period=${period}&limit=${limit}&offset=${offset}${noUndo ? '&noUndo=true' : ''}`;
  const res = await fetch(
    `${API_BASE}/api/scores/${mapType}?${params}`,
  );
  if (!res.ok) throw new Error(`API error: ${res.status}`);
  return res.json();
}
